import React from 'react';
import { Avatar, Text, View } from '@constants/Themed';
import Button from '@components/UI/Button/Button';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import styles, { getModernCardStyle, getModernTextColor, getModernActionStyle } from '@stylesheet';
import getDaysAgo from '@utils/getDaysAgo';
import toTitleCase from '@hooks/toTitleCase';
import moment from 'moment';
import { rejectInvitation, acceptCompanyInvitation, acceptInvitation } from '@api/api';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import * as Animatable from 'react-native-animatable';


const InviteItem = ({ request, onAccept, onReject }) => {
  const theme = themeSelector();
  const globalDate = useSelector((state: DefaultRootState) => state.UI.dateFormat);

  return (
    <Animatable.View
      animation="fadeInUp"
      duration={300}
      style={[
        getModernCardStyle(theme, 'large'),
        {
          marginVertical: 8,
          marginHorizontal: 4,
          padding: 20,
          minHeight: 140,
        }
      ]}
    >
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 16 }}>
        {/* Avatar Container */}
        <Animatable.View
          animation="bounceIn"
          delay={100}
          style={{
            width: 56,
            height: 56,
            borderRadius: 28,
            backgroundColor: theme ? 'rgba(90, 192, 234, 0.1)' : 'rgba(90, 192, 234, 0.2)',
            alignItems: 'center',
            justifyContent: 'center',
            borderWidth: 2,
            borderColor: theme ? 'rgba(90, 192, 234, 0.3)' : 'rgba(90, 192, 234, 0.4)',
          }}
        >
          <Avatar
            src={request.from.profile_information.avatar.img}
            style={{
              width: 48,
              height: 48,
              borderRadius: 24,
            }}
          />
        </Animatable.View>

        {/* Content Container */}
        <View style={{ flex: 1, gap: 12 }}>
          <Animatable.Text
            animation="slideInDown"
            style={{
              fontSize: 16,
              fontWeight: '600',
              color: getModernTextColor(theme, 'primary'),
              lineHeight: 22,
            }}
          >
            <Text style={{ fontWeight: '700', color: getModernTextColor(theme, 'primary') }}>
              {toTitleCase(request.from.profile_information.name) + ' ' + toTitleCase(request.from.profile_information.surname)}
            </Text>
            {' '}invited you to join their {toTitleCase(request.type)}
          </Animatable.Text>

          {/* Type-specific details */}
          <View style={{ gap: 8 }}>
            {request.type === 'data' && (
              <View style={{
                backgroundColor: theme ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.2)',
                padding: 12,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: theme ? 'rgba(59, 130, 246, 0.2)' : 'rgba(59, 130, 246, 0.3)',
              }}>
                <Text style={{
                  fontSize: 14,
                  fontWeight: '600',
                  color: getModernTextColor(theme, 'primary'),
                }}>
                  📊 Entries: {request.entries}
                </Text>
              </View>
            )}

            {request.type === 'project' && (
              <View style={{
                backgroundColor: theme ? 'rgba(34, 197, 94, 0.1)' : 'rgba(34, 197, 94, 0.2)',
                padding: 12,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: theme ? 'rgba(34, 197, 94, 0.2)' : 'rgba(34, 197, 94, 0.3)',
                gap: 8,
              }}>
                <Text style={{
                  fontSize: 14,
                  fontWeight: '600',
                  color: getModernTextColor(theme, 'primary'),
                }}>
                  👥 Participants: {request.participants}
                </Text>
                <Text style={{
                  fontSize: 13,
                  color: getModernTextColor(theme, 'secondary'),
                }}>
                  📅 Start: {moment(request.startDate).format(globalDate)}
                </Text>
                <Text style={{
                  fontSize: 13,
                  color: getModernTextColor(theme, 'secondary'),
                }}>
                  📅 End: {moment(request.endDate).format(globalDate)}
                </Text>
              </View>
            )}

            {request.type === 'company' && request.users && (
              <View style={{
                backgroundColor: theme ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.2)',
                padding: 12,
                borderRadius: 12,
                borderWidth: 1,
                borderColor: theme ? 'rgba(245, 158, 11, 0.2)' : 'rgba(245, 158, 11, 0.3)',
              }}>
                <Text style={{
                  fontSize: 14,
                  fontWeight: '600',
                  color: getModernTextColor(theme, 'primary'),
                }}>
                  🏢 {request.users}
                </Text>
              </View>
            )}
          </View>

          <Text
            style={{
              fontSize: 13,
              color: getModernTextColor(theme, 'muted'),
              fontWeight: '500',
            }}
          >
            {getDaysAgo(request.createdAt)}
          </Text>

          {/* Action Buttons */}
          <View style={{
            flexDirection: 'row',
            gap: 12,
            transform: [{ scale: 0.8 }],
            marginTop: 8,
            alignItems: 'center',
          }}>
           
              <Button
                customStyle={[
                  getModernActionStyle(theme, 'green'),
                  {
                    borderRadius: 12,
                    paddingVertical: 12,
                    paddingHorizontal: 16,
                    minHeight: 44,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }
                ]}
                buttonText="Accept"
                onPress={async () => {
                  if (request.type === 'data') {
                    await acceptInvitation(request._id).then(() => onAccept)
                  } else if (request.type === 'company') {
                    await acceptCompanyInvitation({ id: request._id }).then(() => onAccept)
                  }
                }}
              />
              <Button
                customStyle={[
                  getModernActionStyle(theme, 'red'),
                  {
                    borderRadius: 12,
                    paddingVertical: 12,
                    paddingHorizontal: 16,
                    minHeight: 44,
                    alignItems: 'center',
                    justifyContent: 'center',
                  }
                ]}
                buttonText="Decline"
                onPress={async () => {
                  await rejectInvitation(request._id).then(() => onReject)
                }}
              />
          </View>
        </View>
      </View>
    </Animatable.View>
  );
}
export default InviteItem;